import React, { useState } from 'react';
import { useNotification } from '../contexts/NotificationContext';

type FeedbackType = 'bug' | 'suggestion' | 'general';

export default function Feedback() {
  const { addNotification } = useNotification();
  const [feedbackType, setFeedbackType] = useState<FeedbackType>('general');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!message.trim() || message.length < 10) {
      setError('Please enter a message at least 10 characters long.');
      return;
    }
    setIsSubmitting(true);

    try {
      const response = await fetch('/api/feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ feedback_type: feedbackType, message })
      });
      const data = await response.json() as { error?: string, message?: string };
      if (!response.ok) {
        throw new Error(data.error || 'Failed to submit feedback');
      }
      addNotification(data.message || 'Feedback submitted!', 'success');
      setMessage(''); // Clear form
      setFeedbackType('general');
    } catch (err) {
       const errorMsg = err instanceof Error ? err.message : 'An error occurred';
       setError(errorMsg);
       addNotification(errorMsg, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">Submit Feedback</h1>
      
      <form onSubmit={handleSubmit} className="card p-6 space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Feedback Type</label>
          <div className="flex space-x-4">
            {(['bug', 'suggestion', 'general'] as FeedbackType[]).map(type => (
              <button
                key={type}
                type="button"
                onClick={() => setFeedbackType(type)}
                className={`px-4 py-2 rounded-md text-sm capitalize 
                  ${feedbackType === type 
                    ? 'bg-primary text-white' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600'}
                `}
              >
                {type}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label htmlFor="message" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Message (min. 10 characters)
          </label>
          <textarea
            id="message"
            rows={6}
            className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-primary focus:ring-primary sm:text-sm dark:bg-gray-700 dark:text-gray-100"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            required
          />
        </div>

        {error && (
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
        )}

        <div className="flex justify-end">
          <button 
            type="submit" 
            className="btn-primary" 
            disabled={isSubmitting || message.length < 10}
          >
            {isSubmitting ? (
                <span className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Submitting...
                </span>
            ) : ( 'Submit Feedback' )}
          </button>
        </div>
      </form>
    </div>
  );
} 